/**
 * Copyright Highway9 Networks Inc.
 */
import { createEntityAdapter, createSelector, createSlice, type EntityState, type PayloadAction } from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack";
import { RootState } from "..";
import { createNotificationToast, mapOperationStatus, NOTIFICATION_MAX_HISTORY } from "~/components/Notifications/utils";
import { Notification, NotificationSource, NotificationStatus, NotificationUpdate } from "~/types/Notification";

const OPERATION_TOAST_EXCEPTIONS = new Set(["IMPORT_SITE"]);

const FINAL_STATUSES = new Set<NotificationStatus>(["completed", "failed", "cancelled"]);

export const notificationsAdapter = createEntityAdapter<Notification>({
  sortComparer: (a, b) => b.timestamp - a.timestamp, // Sort by timestamp in descending order
});

interface NotificationState {
  notificationsData: EntityState<Notification>;
  /** Secondary index: operationId → notification id for O(1) websocket sync. */
  byOperationId: Record<string, string>;
  /** Identity only — resolve via selectLatestNotification. */
  latestId: string | null;
}

const initialState: NotificationState = {
  notificationsData: notificationsAdapter.getInitialState(),
  byOperationId: {},
  latestId: null,
};

const indexOperationId = (
  state: NotificationState,
  notificationId: string,
  operationId?: string,
) => {
  if (operationId) {
    state.byOperationId[operationId] = notificationId;
  }
};

const unindexOperationId = (state: NotificationState, operationId?: string) => {
  if (operationId) {
    delete state.byOperationId[operationId];
  }
};

const rebuildOperationIndex = (state: NotificationState) => {
  const index: Record<string, string> = {};
  for (const id of state.notificationsData.ids) {
    const operationId = state.notificationsData.entities[id]?.operationId;
    if (operationId) {
      index[operationId] = id as string;
    }
  }
  state.byOperationId = index;
};

const trimHistory = (state: NotificationState) => {
  const { ids } = state.notificationsData;
  if (ids.length <= NOTIFICATION_MAX_HISTORY) return;

  const removedIds = ids.slice(NOTIFICATION_MAX_HISTORY) as string[];
  for (const id of removedIds) {
    unindexOperationId(state, state.notificationsData.entities[id]?.operationId);
    if (state.latestId === id) {
      state.latestId = null;
    }
  }
  notificationsAdapter.removeMany(state.notificationsData, removedIds);
};

const toastVariantFor = (
  status: NotificationStatus,
): "success" | "error" | "info" => {
  switch (status) {
    case "completed":
      return "success";
    case "failed":
      return "error";
    case "queued":
    case "in-progress":
    case "cancelled":
      return "info";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    bulkAddNotifications: (state, action: PayloadAction<Notification[]>) => {
      notificationsAdapter.setAll(state.notificationsData, action.payload);
      rebuildOperationIndex(state);
      trimHistory(state);
    },

    /**
     * Add a new notification
     */
    addNotification: (state, action: PayloadAction<Notification>) => {
      const notification = action.payload;
      notificationsAdapter.upsertOne(state.notificationsData, notification);
      indexOperationId(state, notification.id, notification.operationId);
      trimHistory(state);
      state.latestId = notification.id;
    },

    updateNotificationByOperationId: (
      state,
      action: PayloadAction<{ operationId: string; updates: NotificationUpdate }>,
    ) => {
      const id = state.byOperationId[action.payload.operationId];
      if (!id) return;
      notificationsAdapter.updateOne(state.notificationsData, {
        id,
        changes: action.payload.updates,
      });
    },

    setLatestId: (state, action: PayloadAction<string | null>) => {
      state.latestId = action.payload;
    },

    markAsRead: (state, action: PayloadAction<string>) => {
      notificationsAdapter.updateOne(state.notificationsData, {
        id: action.payload,
        changes: { read: true },
      });
    },

    markAllAsRead: (state) => {
      notificationsAdapter.updateMany(
        state.notificationsData,
        state.notificationsData.ids.map((id) => ({
          id: id as string,
          changes: { read: true },
        })),
      );
    },

    /** Sync notification with operation data (called from websocket events). */
    syncWithOperation: (
      state,
      action: PayloadAction<{
        operationId: string;
        operationName?: string;
        source: string;
        status: string;
        statusMessage?: string;
        progress?: number;
        output?: unknown;
        input?: unknown;
        completionTime?: number;
      }>,
    ) => {
      const id = state.byOperationId[action.payload.operationId];
      if (!id) return;

      const notification = state.notificationsData.entities[id];
      if (!notification) return;

      const {
        status,
        statusMessage,
        source,
        progress,
        output,
        input,
        completionTime,
        operationName,
      } = action.payload;

      // Operations are patched on every websocket flush, so a patch that only
      // refreshes progress, statusMessage or file stats must not toast or
      // re-surface the bell - only a real status transition may (MCS-4316)
      const previousStatus = notification.status;
      const newStatus = status ? mapOperationStatus(status) : previousStatus;
      const statusChanged = !!status && newStatus !== previousStatus;

      if (status) notification.status = newStatus;
      if (statusMessage) notification.subtitle = statusMessage;
      if (progress !== undefined) notification.progress = progress;
      if (source) notification.source = source as NotificationSource;
      if (output) {
        notification.metadata = {
          ...notification.metadata,
          output,
          input,
        };
      }

      if (status && FINAL_STATUSES.has(newStatus)) {
        const { timestamp } = notification;
        if (completionTime && timestamp && completionTime > 0 && timestamp > 0) {
          notification.completedIn = Math.max(0, completionTime - timestamp);
        }
      }

      if (!statusChanged) return;

      notification.read = false;
      state.latestId = id;

      const isExceptionType =
        !!operationName && OPERATION_TOAST_EXCEPTIONS.has(operationName);
      if (!isExceptionType || FINAL_STATUSES.has(newStatus)) {
        enqueueSnackbar(createNotificationToast(notification), {
          variant: toastVariantFor(newStatus),
        });
      }
    },
  },
});

export const notificationActions = notificationsSlice.actions;
export default notificationsSlice.reducer;

// Selectors

const notificationEntitySelectors = notificationsAdapter.getSelectors(
  (state: RootState) => state.notifications.notificationsData,
);

export const selectAllNotifications = notificationEntitySelectors.selectAll;
export const selectNotificationEntities =
  notificationEntitySelectors.selectEntities;

/**
 * Select the latest notification (resolved from latestId)
 */
export const selectLatestNotification = createSelector(
  [
    (state: RootState) => state.notifications.latestId,
    selectNotificationEntities,
  ],
  (latestId, entities): Notification | null =>
    latestId ? (entities[latestId] ?? null) : null,
);

/** O(1) lookup via byOperationId index — preferred for websocket handlers. */
export const selectNotificationByOperationId = (
  state: RootState,
  operationId: string,
): Notification | undefined => {
  const id = state.notifications.byOperationId[operationId];
  return id ? state.notifications.notificationsData.entities[id] : undefined;
};

export const selectUnreadNotificationCount = createSelector(
  [selectAllNotifications],
  (notifications) => notifications.reduce((count, n) => count + (n.read ? 0 : 1), 0),
);
